import { useCallback, useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Flex,
  Box,
} from '@chakra-ui/react';
import { PhoneInput, Button, SectionLabel } from '@kula-ai/components';
import { validatePhoneNumber } from '@kula-ai/components';
// import { TimeZoneSelectorV1 } from '@/components/formBuilder/formFields/DateField/components/TimeZoneSelectorV1';
import { KText } from '@/ui-library/components/text';
import { useUpdateApplicationDetailsMutation } from '@/store/query/ats-applications';
import { useShowToast } from '@/hooks';
import '@/components/formBuilder/formFields/KPhoneField/KPhoneField.css';

const TITLE = 'No phone number and timezone.';
const DESCRIPTION =
  'Candidate needs a valid phone number and timezone setup to be called by Kula Coordinator.';

/** PhoneInput value shape: { countryCode, dialCode, number } */
function formatPhoneForApi(phoneValue) {
  if (!phoneValue?.dialCode || !phoneValue?.number?.trim()) return null;
  return `${phoneValue.dialCode}${phoneValue.number.trim().replace(/\s/g, '')}`;
}

export interface CollectPhoneTimezoneModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** ATS application id — used for PUT api_ats/v1/ats_applications/:applicationId */
  applicationId: number | null;
  /** Contact (candidate) for display; time_zone used as initial value */
  contact: {
    id: number;
    local_phone_number?: string;
    phone_number?: string;
    time_zone?: string;
  } | null;
  /** Called after successful update; can then open Schedule with AI flow */
  onSuccess?: () => void;
}

export function CollectPhoneTimezoneModal({
  isOpen,
  onClose,
  applicationId,
  contact,
  onSuccess,
}: CollectPhoneTimezoneModalProps) {
  const toast = useShowToast();
  const [updateApplicationDetails, { isLoading }] =
    useUpdateApplicationDetailsMutation();

  const [phoneValue, setPhoneValue] = useState<
    { countryCode: string; dialCode: string; number: string } | undefined
  >(undefined);
  const [timezoneValue, setTimezoneValue] = useState<{
    label: string;
    key: string;
  } | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const phoneWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const nextTimezone = contact?.time_zone
      ? { label: contact.time_zone, key: contact.time_zone }
      : null;
    queueMicrotask(() => {
      setPhoneValue(undefined);
      setTimezoneValue(nextTimezone);
      setPhoneError(null);
    });
  }, [isOpen, contact?.id, contact?.time_zone]);

  const handlePhoneChange = useCallback((value) => {
    setPhoneValue(value);
    setPhoneError(null);
  }, []);

  // const handleTimezoneChange = useCallback(
  //   (value: { label: string; key: string }) => {
  //     setTimezoneValue(value);
  //   },
  //   []
  // );

  const validate = useCallback(() => {
    if (!phoneValue?.number?.trim()) {
      setPhoneError('Phone number is required');
      return false;
    }
    const result = validatePhoneNumber(
      phoneValue.number,
      phoneValue.countryCode
    );
    if (!result.isValid) {
      setPhoneError(result.error || 'Invalid phone number');
      return false;
    }
    // Timezone field hidden for now – no longer required
    // if (!timezoneValue?.key) {
    //   toast({ title: 'Please select a time zone', status: 'error' });
    //   return false;
    // }
    return true;
  }, [phoneValue]);

  const handleUpdate = useCallback(async () => {
    if (!validate() || !applicationId) return;

    const phoneNumber = formatPhoneForApi(phoneValue);
    if (!phoneNumber) return;

    try {
      const contactPayload: {
        phone_number: string;
        time_zone?: string;
        country_code?: string;
      } = {
        phone_number: phoneNumber,
        ...(timezoneValue?.key && { time_zone: timezoneValue.key }),
      };
      if (phoneValue?.countryCode) {
        contactPayload.country_code = phoneValue.countryCode;
      }
      await updateApplicationDetails({
        applicationId,
        payload: { contact: contactPayload },
      }).unwrap();

      toast({ title: "We've saved your phone number and timezone." });
      onSuccess?.();
      onClose();
    } catch {
      toast({ title: "We couldn't save. Please try again.", status: 'error' });
    }
  }, [
    validate,
    applicationId,
    phoneValue,
    timezoneValue,
    updateApplicationDetails,
    toast,
    onSuccess,
    onClose,
  ]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontWeight="600" fontSize="16px">
          {TITLE}
        </ModalHeader>
        <ModalBody>
          <KText size="sm-medium" color="secondary-700" mb="16px">
            {DESCRIPTION}
          </KText>
          <Flex flexDirection="column" gap="16px">
            <Box>
              <SectionLabel>Phone number</SectionLabel>
              <Box
                ref={phoneWrapperRef}
                mt="8px"
                className="k-phone-field-wrapper"
              >
                <PhoneInput
                  value={phoneValue}
                  onChange={handlePhoneChange}
                  defaultCountry="US"
                  placeholder="Add phone number"
                  size="md"
                  error={!!phoneError}
                  name="phone"
                  id="collect-phone-timezone-phone"
                />
              </Box>
              {phoneError && (
                <Box mt="6px" fontSize="12px" color="pastel-1-dark">
                  {phoneError}
                </Box>
              )}
            </Box>
            {/* Timezone field hidden for now – uncomment when needed
            <Box>
              <TimeZoneSelectorV1
                value={timezoneValue}
                onChange={handleTimezoneChange}
                inputProps={{ menuPlacement: 'auto' }}
              />
            </Box>
            */}
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Flex gap="12px" justifyContent="flex-end">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleUpdate}
              disabled={isLoading}
              isLoading={isLoading}
            >
              Update
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
